import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Store, LayoutDashboard, ShoppingCart, History, Package, 
  ArrowRightLeft, FileText, LogOut, Menu, X, User, ChevronDown, 
  Settings, Key, DollarSign, Building2, Info, Receipt, AlertTriangle, Monitor, Users, ClipboardList, Bell, Wallet, Truck, ClipboardCheck, BarChart3, CalendarClock
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import ChangePasswordDialog from '@/components/ChangePasswordDialog';
import { StoreTerminalProvider, TerminalIndicator, useStoreTerminal } from '@/components/store/TerminalSelector';

// Terminal Header Bar Component
const TerminalHeaderBar = () => {
  const terminalContext = useStoreTerminal();
  
  // Don't show header bar if context not loaded
  if (!terminalContext) return null;
  
  const { currentTerminal, loading } = terminalContext;
  
  // Always show the header bar (even when loading or no terminal selected)
  return (
    <div className="bg-white border-b border-gray-200 px-6 py-2 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <TerminalIndicator />
      </div>
      {currentTerminal?.has_open_register && (
        <span className="text-xs text-green-600 font-medium">
          Register Open
        </span>
      )}
    </div>
  );
};

const StoreLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login/store');
  };

  // Check user role and store type
  const isManager = user?.role_name?.toLowerCase().includes('manager');
  const isCashier = user?.role_name?.toLowerCase().includes('cashier');
  const isFranchise = user?.store_type === 'franchise';

  // Define all menu items with permission keys
  const allMenuItems = [
    { 
      path: '/store/dashboard', 
      icon: LayoutDashboard, 
      label: 'Dashboard',
      description: 'Sales overview & stats',
      permission: 'dashboard'
    },
    { 
      path: '/store/pos', 
      icon: ShoppingCart, 
      label: 'POS Billing',
      description: 'Point of Sale',
      highlight: true,
      permission: 'pos'
    },
    { 
      path: '/store/register', 
      icon: DollarSign, 
      label: 'Cash Register',
      description: 'Open/Close shift',
      permission: 'register'
    },
    { 
      path: '/store/sales', 
      icon: History, 
      label: 'Sales History',
      description: 'View past bills',
      permission: 'sales'
    },
    { 
      path: '/store/customers', 
      icon: Users, 
      label: 'Customers',
      description: 'Manage customers',
      permission: 'customers',
      managerOnly: true
    },
    { 
      path: '/store/stock', 
      icon: Package, 
      label: 'Store Stock',
      description: 'View inventory',
      permission: 'stock'
    },
    { 
      path: '/store/stock-audit', 
      icon: ClipboardCheck, 
      label: 'Stock Audit',
      description: 'Physical count vs system',
      permission: 'stock',
      managerOnly: true
    },
    { 
      path: '/store/stock-requests', 
      icon: ShoppingCart, 
      label: 'Stock Requests',
      description: 'Request stock from HQ',
      permission: 'transfers',
      managerOnly: true,
      ownedStoreOnly: true  // Only for owned stores - franchises use Purchase Orders
    },
    { 
      path: '/store/transfers', 
      icon: ArrowRightLeft, 
      label: 'Stock Receipts',
      description: 'Receive transfers',
      permission: 'transfers',
      ownedStoreOnly: true  // Only for owned stores - franchises receive via PO workflow
    },
    { 
      path: '/store/expenses', 
      icon: Receipt, 
      label: 'Expenses',
      description: 'Track expenses',
      permission: 'expenses'
    },
    { 
      path: '/store/damaged-stock', 
      icon: AlertTriangle, 
      label: 'Damaged Stock',
      description: 'Report damages',
      permission: 'damaged_stock'
    },
    {
      path: '/store/low-stock/alerts',
      icon: Bell,
      label: 'Low Stock Alerts',
      description: 'View stock alerts',
      permission: 'stock',
      managerOnly: true
    },
    {
      path: '/store/low-stock/settings',
      icon: Settings,
      label: 'Alert Settings',
      description: 'Configure thresholds',
      permission: 'stock',
      managerOnly: true
    },
    {
      path: '/store/pricing',
      icon: DollarSign,
      label: 'Price Variance',
      description: 'Manage price variance',
      permission: 'stock',
      managerOnly: true,
      franchiseOnly: true
    },
    {
      path: '/store/local-vendors',
      icon: Users,
      label: 'Local Vendors',
      description: 'Manage local suppliers',
      permission: 'stock',
      managerOnly: true,
      franchiseOnly: true  // Franchise stores manage their own local vendors
    },
    {
      path: '/store/hq-local-vendors',
      icon: Truck,
      label: 'Local Vendors',
      description: 'HQ assigned vendors',
      permission: 'stock',
      managerOnly: true,
      ownedStoreOnly: true  // Own stores use HQ-assigned local vendors
    },
    {
      path: '/store/local-purchase',
      icon: ShoppingCart,
      label: 'Local Purchase',
      description: 'Buy from local vendors',
      permission: 'stock',
      managerOnly: true,
      ownedStoreOnly: true  // Own stores can create local POs
    },
    {
      path: '/store/purchase-orders',
      icon: ShoppingCart,
      label: 'Purchase Orders',
      description: 'Order from vendors & HQ',
      permission: 'stock',
      managerOnly: true
    },
    {
      path: '/store/grn-entry',
      icon: Package,
      label: 'GRN Entry',
      description: 'Goods receipt from all sources',
      permission: 'stock',
      managerOnly: true
    },
    {
      path: '/store/vendor-ledger',
      icon: Wallet,
      label: 'Vendor Ledger',
      description: 'Vendor balances & payments',
      permission: 'stock',
      managerOnly: true
    },
    {
      path: '/store/reports/sales',
      icon: BarChart3,
      label: 'Sales Report',
      description: 'Product & date wise sales',
      permission: 'dashboard',
      managerOnly: true
    },
    {
      path: '/store/reports/expiry',
      icon: CalendarClock,
      label: 'Stock Expiry',
      description: 'Expiring & expired stock',
      permission: 'stock',
      managerOnly: true
    },
    {
      path: '/store/franchise-supply',
      icon: Truck,
      label: 'Franchise Supply',
      description: 'Process franchise orders',
      permission: 'stock',
      managerOnly: true,
      ownedStoreOnly: true  // Only owned stores fulfill franchise orders
    },
    {
      path: '/store/stock-transfer',
      icon: ArrowRightLeft,
      label: 'Stock Transfer',
      description: 'Transfer to owned stores',
      permission: 'stock',
      managerOnly: true,
      ownedStoreOnly: true  // Only owned stores can transfer to other owned stores
    },
    {
      path: '/store/hq-payments',
      icon: Wallet,
      label: 'HQ Payments',
      description: 'Pay outstanding balance',
      permission: 'stock',
      managerOnly: true,
      franchiseOnly: true  // Only for franchise stores
    },
    { 
      path: '/store/settings', 
      icon: Settings, 
      label: 'Settings',
      description: 'Terminals & config',
      permission: 'settings'
    },
    { 
      path: '/store/reports', 
      icon: FileText, 
      label: 'Reports',
      description: 'Sales & stock reports',
      permission: 'reports'
    },
    { 
      path: '/store/info', 
      icon: Info, 
      label: 'Store Info',
      description: 'View store details',
      permission: 'info'
    },
  ];
  
  // Define cashier-allowed permissions (only POS and Cash Register)
  const cashierAllowedPermissions = ['pos', 'register'];
  
  // Filter menu items based on role and store type
  const menuItems = allMenuItems.filter(item => {
    // Cashiers can only see specific items
    if (isCashier && !cashierAllowedPermissions.includes(item.permission)) {
      return false;
    }
    // Manager-only items only visible to managers
    if (item.managerOnly && !isManager) {
      return false;
    }
    // Franchise-only items hidden for owned stores
    if (item.franchiseOnly && !isFranchise) {
      return false;
    }
    // Owned-store-only items hidden for franchise stores
    if (item.ownedStoreOnly && isFranchise) {
      return false;
    }
    return true;
  });

  // Get role display name
  const getRoleDisplayName = () => {
    if (isCashier) return 'Cashier';
    if (isManager) return 'Store Manager';
    return user?.role_name || 'Staff';
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  return (
    <div className="min-h-screen bg-gray-100 flex" data-testid="store-layout">
      {/* Sidebar */}
      <aside 
        className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-emerald-800 to-emerald-900 text-white transition-all duration-300 flex flex-col fixed h-full z-20`}
      >
        {/* Logo */}
        <div className="p-4 border-b border-emerald-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-600 flex items-center justify-center">
              <Store className="w-6 h-6" />
            </div>
            {sidebarOpen && (
              <div>
                <h1 className="font-bold text-lg">SY-Store</h1>
                <p className="text-xs text-emerald-300">Store POS</p>
              </div>
            )}
          </div>
        </div>

        {/* Store Info */}
        {sidebarOpen && (
          <div className="p-4 border-b border-emerald-700 bg-emerald-700/30">
            <p className="text-emerald-200 text-xs uppercase tracking-wide">Store</p>
            <p className="font-semibold text-white truncate">{user?.entity_name || 'Store'}</p>
            <p className="text-xs text-emerald-300">{user?.entity_code}</p>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 mx-2 rounded-lg transition-all duration-200
                ${isActive(item.path) 
                  ? 'bg-emerald-600 text-white shadow-lg' 
                  : 'text-emerald-200 hover:bg-emerald-700/50 hover:text-white'}
                ${item.highlight && !isActive(item.path) ? 'border border-emerald-500/50' : ''}
              `}
            >
              <item.icon className={`w-5 h-5 ${item.highlight ? 'text-emerald-300' : ''}`} />
              {sidebarOpen && (
                <div>
                  <span className="font-medium">{item.label}</span>
                  <p className="text-xs text-emerald-300">{item.description}</p>
                </div>
              )}
            </Link>
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-emerald-700">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full text-left p-2 rounded-lg hover:bg-emerald-700/50 transition-colors">
                <div className="w-8 h-8 rounded-full bg-emerald-600 flex items-center justify-center">
                  <User className="w-4 h-4" />
                </div>
                {sidebarOpen && (
                  <>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{user?.full_name || user?.username}</p>
                      <p className="text-xs text-emerald-300 truncate">{getRoleDisplayName()}</p>
                    </div>
                    <ChevronDown className="w-4 h-4 text-emerald-400" />
                  </>
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56 bg-white border-gray-200">
              <DropdownMenuItem 
                onClick={() => setPasswordDialogOpen(true)}
                className="cursor-pointer"
              >
                <Key className="w-4 h-4 mr-2" />
                Change Password
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem 
                onClick={handleLogout}
                className="text-red-600 cursor-pointer"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Toggle Button */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute -right-3 top-20 bg-emerald-700 text-white p-1 rounded-full shadow-lg hover:bg-emerald-600 transition-colors"
        >
          {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
        </button>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 ${sidebarOpen ? 'ml-64' : 'ml-20'} transition-all duration-300`}>
        {/* Terminal Header Bar */}
        <TerminalHeaderBar />
        {children}
      </main>

      {/* Change Password Dialog */}
      <ChangePasswordDialog 
        open={passwordDialogOpen} 
        onOpenChange={setPasswordDialogOpen}
      />
    </div>
  );
};

// Wrap with Terminal Provider
const StoreLayoutWithTerminals = ({ children }) => (
  <StoreTerminalProvider>
    <StoreLayout>{children}</StoreLayout>
  </StoreTerminalProvider>
);

export default StoreLayoutWithTerminals;
